import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import api, { API_BASE } from "@/lib/api";
import AppShell from "@/components/AppShell";
import RichEditor from "@/components/RichEditor";
import { toast } from "sonner";
import { Save, Sparkles, ExternalLink, ArrowLeft, Upload, X } from "lucide-react";

export default function DeliveryEditor() {
  const { id } = useParams();
  const [lm, setLm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    api.get(`/lead-magnets/${id}`).then((r) => setLm(r.data)).catch(() => toast.error("Failed to load"));
  }, [id]);

  const update = (patch) => setLm((p) => ({ ...p, delivery_config: { ...p.delivery_config, ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/lead-magnets/${id}`, { delivery_config: lm.delivery_config });
      toast.success("Delivery saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const aiGenerate = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/ai/generate", { kind: "delivery_content", topic: lm.title });
      const d = r.data.data || {};
      update({
        title: d.title || lm.delivery_config.title,
        intro: d.intro || lm.delivery_config.intro,
        rich_html: d.rich_html || lm.delivery_config.rich_html,
      });
      toast.success("AI delivery content generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI failed");
    } finally { setAiLoading(false); }
  };

  if (!lm) return <AppShell><div className="text-zinc-500">Loading…</div></AppShell>;
  const cfg = lm.delivery_config || {};
  const publicDeliveryUrl = `${window.location.origin}/lm/${lm.slug}/delivery`;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error("Max 100 MB per video"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "video");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${API_BASE.replace(/\/api$/, "")}${r.data.url}`;
      update({ video_url: fullUrl });
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  return (
    <AppShell>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <Link to={`/builder/${id}`} className="text-sm font-bold inline-flex items-center gap-2 mb-2" data-testid="back-builder"><ArrowLeft size={14} /> Back to Builder</Link>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">Delivery Editor — {lm.title}</h1>
        </div>
        <div className="flex gap-2">
          <a href={publicDeliveryUrl} target="_blank" rel="noreferrer" className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid="preview-delivery-btn"><ExternalLink size={14} /> Preview</a>
          <button onClick={aiGenerate} disabled={aiLoading} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid="ai-delivery-btn"><Sparkles size={14} /> {aiLoading ? "Generating…" : "AI Content"}</button>
          <button onClick={save} disabled={saving} className="btn-brutal !px-3 !py-2 text-sm" data-testid="save-delivery-btn"><Save size={14} /> Save</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-brutal space-y-4">
          <Field label="Page Title" value={cfg.title} onChange={(v) => update({ title: v })} testid="delivery-title" />
          <div>
            <label className="label-cap block mb-2">Intro paragraph</label>
            <textarea rows={3} value={cfg.intro || ""} onChange={(e) => update({ intro: e.target.value })} className="input-brutal" data-testid="delivery-intro" />
          </div>
          <div>
            <label className="label-cap block mb-2">Video URL (MP4 / YouTube / Vimeo)</label>
            <div className="flex gap-2">
              <input value={cfg.video_url || ""} onChange={(e) => update({ video_url: e.target.value })} className="input-brutal flex-1" placeholder="Paste URL or upload" data-testid="delivery-video-url" />
              {cfg.video_url && (
                <button onClick={() => update({ video_url: "" })} className="btn-brutal-outline !px-3" title="Clear" data-testid="clear-video-btn"><X size={14} /></button>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input ref={fileInput} type="file" accept="video/mp4,video/quicktime,video/webm" onChange={handleUpload} className="hidden" data-testid="video-file-input" />
              <button onClick={() => fileInput.current?.click()} disabled={uploading} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid="upload-video-btn">
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload MP4 (max 100 MB)"}
              </button>
            </div>
            <div className="text-xs text-zinc-500 mt-1">Direct .mp4 URLs render with native HTML5 player. YouTube/Vimeo embed automatically.</div>
          </div>
          <Field label="Optional download URL (PDF, etc.)" value={cfg.download_url} onChange={(v) => update({ download_url: v })} testid="delivery-download-url" />
          <div>
            <label className="label-cap block mb-2">Rich content</label>
            <RichEditor value={cfg.rich_html || ""} onChange={(html) => update({ rich_html: html })} />
            <div className="text-xs text-zinc-500 mt-1">Format with the toolbar. Click "AI Content" above for instant generation.</div>
          </div>
        </div>

        <div>
          <div className="label-cap mb-3">// Live preview</div>
          <div className="preview-frame max-h-[800px] overflow-y-auto bg-white">
            <DeliveryPreview cfg={cfg} />
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-cap block mb-2">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-brutal" data-testid={testid} />
    </div>
  );
}

function DeliveryPreview({ cfg }) {
  return (
    <article className="max-w-3xl mx-auto px-6 md:px-10 py-12">
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-4">{cfg.title || "Your Resource"}</h1>
      <p className="text-zinc-700 mb-6 text-lg">{cfg.intro}</p>
      {cfg.video_url && <VideoEmbed url={cfg.video_url} />}
      {cfg.download_url && (
        <a href={cfg.download_url} target="_blank" rel="noreferrer" className="btn-brutal mb-6 mt-2 inline-flex">Download File</a>
      )}
      <div className="prose-vibe mt-6" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cfg.rich_html || "", { USE_PROFILES: { html: true } }) }} />
      <style>{`
        .prose-vibe h2 { font-family: 'Outfit', sans-serif; font-weight: 800; font-size: 1.75rem; margin-top: 2rem; margin-bottom: 0.75rem; letter-spacing: -0.02em; }
        .prose-vibe h3 { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 1.25rem; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .prose-vibe p { margin-bottom: 1rem; line-height: 1.7; color: #27272a; }
        .prose-vibe ul { margin: 1rem 0; padding-left: 1.5rem; list-style: disc; }
        .prose-vibe ul li { margin-bottom: 0.5rem; line-height: 1.7; }
        .prose-vibe strong { font-weight: 800; color: #0A0A0A; }
        .prose-vibe a { color: #FF4F00; font-weight: 700; text-decoration: underline; }
      `}</style>
    </article>
  );
}

function VideoEmbed({ url }) {
  if (!url) return null;
  const isYT = /youtu\.?be/.test(url);
  const isVimeo = /vimeo\.com/.test(url);
  if (isYT) {
    const id = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/)?.[1];
    if (!id) return null;
    return (
      <div className="aspect-video border-2 border-black shadow-brutal mb-4">
        <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${id}`} allowFullScreen title="Video" />
      </div>
    );
  }
  if (isVimeo) {
    const id = url.match(/vimeo\.com\/(\d+)/)?.[1];
    if (!id) return null;
    return (
      <div className="aspect-video border-2 border-black shadow-brutal mb-4">
        <iframe className="w-full h-full" src={`https://player.vimeo.com/video/${id}`} allowFullScreen title="Video" />
      </div>
    );
  }
  return (
    <video src={url} controls className="w-full aspect-video border-2 border-black shadow-brutal mb-4" data-testid="delivery-video-player" />
  );
}

export { DeliveryPreview };
